import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../Helpers/Pagination";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const InventoryReport = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  /* ================= FILTER STATE ================= */
  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  /* ================= LOAD INVENTORY ================= */
  const loadInventory = async ({ today, dateFrom, dateTo, page }) => {

    dispatch(loadingTrue());

    const params = new URLSearchParams();
    params.append("org_id", user.org_id);
    params.append("page", page);
    params.append("limit", limit);

    if (today) params.append("today", 1);
    if (dateFrom) params.append("date_from", dateFrom.toISOString().slice(0, 10));
    if (dateTo) params.append("date_to", dateTo.toISOString().slice(0, 10));

    const res = await Handler({
      method: "get",
      url: `/reports/inventory_report.php?${params.toString()}`
    });


    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed");
      return;
    }

    setRows(res.data?.rows || []);
    setTotals(res.data?.totals || null);
    setTotal(res.data?.pagination?.total_rows || 0);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.org_id) {
      setPage(1);
      loadInventory({ today, dateFrom, dateTo, page: 1 });
    }
  }, [user?.org_id]);

  /* ================= AUTO FILTER ================= */
  useEffect(() => {
    if (!user?.org_id) return;
    setPage(1);
    loadInventory({ today, dateFrom, dateTo, page: 1 });
  }, [today, dateFrom, dateTo, user?.org_id]);

  /* ================= PAGE CHANGE ================= */
  const handlePageChange = (p) => {
    setPage(p);
    loadInventory({ today, dateFrom, dateTo, page: p });
  };

  /* ================= HANDLERS ================= */
  const handleToday = () => {
    setToday(!today);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleDateFrom = (d) => {
    setToday(false);
    setDateFrom(d);
  };

  const handleDateTo = (d) => {
    setToday(false);
    setDateTo(d);
  };

  /* ================= CHART DATA ================= */
  const chartData = rows.map(r => ({
    name: r.name,
    SoldQty: r.sold_qty,
    SoldValue: r.sold_value
  }));

  return (
    <>

      {/* ================= FILTER BAR ================= */}
      <div className="filter-bar mb-4">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Button
              color={today ? "primary" : "outline-primary"}
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>

          <Col md={3} className="gap-2 d-flex align-items-center">
            <label className="small">Date From</label>
            <DatePicker
              selected={dateFrom}
              onChange={handleDateFrom}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
            />
          </Col>

          <Col md={3} className="gap-2 d-flex align-items-center">
            <label className="small">Date To</label>
            <DatePicker
              selected={dateTo}
              onChange={handleDateTo}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
              minDate={dateFrom}
            />
          </Col>
        </Row>
      </div>

      {/* ================= KPI CARDS ================= */}
      {totals && (
        <Row className="mb-4 g-3">
          <Col md={6}><div className="kpii-card kpii-blue"><div className="kpii-label">Total Products</div><div className="kpii-value">{totals.products}</div></div></Col>
          {/* <Col md={4}><div className="kpii-card kpii-purple"><div className="kpii-label">Total Sold Qty</div><div className="kpii-value">{totals.sold_qty}</div></div></Col> */}
          <Col md={6}><div className="kpii-card kpii-green"><div className="kpii-label">Net Sale Value</div><div className="kpii-value">₹ {Number(totals.sold_value).toFixed(2)}</div></div></Col>
        </Row>
      )}

      {/* ================= BAR CHART ================= */}
      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={10}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="SoldQty" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="SoldValue" fill="#22c55e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <div className="billing-table-card">
        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Outlet</th>
                <th className="right">Price</th>
                <th className="right">Sold Qty</th>
                <th className="right">Sold Value</th>
              </tr>
            </thead>
            <tbody>
              {rows && rows?.map(r => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.outlet_name}</td>
                  <td className="right">₹ {Number(r.price).toFixed(2)}</td>
                  <td className={`right ${r.sold_qty === 0 ? "muted" : ""}`}>{r.sold_qty}</td>
                  <td className={`right ${r.sold_value > 0 ? "success" : "muted"}`}>
                    ₹ {Number(r.sold_value).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ================= PAGINATION ================= */}
        <Pagination
          page={page}
          total={total}
          limit={limit}
          onChange={handlePageChange}
        />
      </div>

    </>
  );
};

export default InventoryReport;
